import { getCategoryStats, getPointsEvolution } from "@/services/stats.service";
import { getEnrolledModules, getAvailableModules, getEnrolledModulesProgress } from "@/services/modules.service";

export const namespaced = true;

const TTL_MS = 5 * 60 * 1000; // 5 minutes
const REFETCH_AFTER_EXERCISES = 10;

export const state = {
  stats: null,
  enrolledModules: [],
  availableModules: [],
  pointsEvolution: null,
  fetchedAt: null,
  exercisesCompletedSinceLastFetch: 0,
  fetchPromise: null,
};

export const getters = {
  isCacheFresh(state) {
    if (!state.fetchedAt) return false;
    const age = Date.now() - state.fetchedAt;
    if (age >= TTL_MS) return false;
    if (state.exercisesCompletedSinceLastFetch >= REFETCH_AFTER_EXERCISES) return false;
    return true;
  },
  hasCache(state) {
    return state.fetchedAt != null;
  },
};

export const mutations = {
  SET_DASHBOARD_DATA(state, payload) {
    if (payload.stats != null) state.stats = payload.stats;
    if (payload.enrolledModules != null) state.enrolledModules = payload.enrolledModules;
    if (payload.availableModules != null) state.availableModules = payload.availableModules;
    if (payload.pointsEvolution != null) state.pointsEvolution = payload.pointsEvolution;
    state.fetchedAt = Date.now();
    state.exercisesCompletedSinceLastFetch = 0;
  },
  CLEAR_CACHE(state) {
    state.stats = null;
    state.enrolledModules = [];
    state.availableModules = [];
    state.pointsEvolution = null;
    state.fetchedAt = null;
    state.exercisesCompletedSinceLastFetch = 0;
    state.fetchPromise = null;
  },
  INCREMENT_EXERCISES_COMPLETED(state) {
    state.exercisesCompletedSinceLastFetch += 1;
  },
  SET_FETCH_PROMISE(state, promise) {
    state.fetchPromise = promise;
  },
  CLEAR_FETCH_PROMISE(state) {
    state.fetchPromise = null;
  },
  SET_ENROLLED_MODULES_PROGRESS(state, payload) {
    if (!Array.isArray(payload)) return;
    const byId = new Map(payload.map((p) => [p.moduleId, p.progressPercentage]));
    state.enrolledModules = state.enrolledModules.map((m) => ({
      ...m,
      progressPercentage: byId.has(m.id) ? byId.get(m.id) : m.progressPercentage,
    }));
  },
};

export const actions = {
  async fetchDashboardDataIfNeeded({ state, getters, commit }) {
    if (getters.isCacheFresh) {
      return {
        stats: state.stats,
        enrolledModules: state.enrolledModules,
        availableModules: state.availableModules,
        pointsEvolution: state.pointsEvolution,
      };
    }
    if (state.fetchPromise) {
      return state.fetchPromise;
    }
    const promise = (async () => {
      try {       
        const [stats, enrolledModules, availableModules, pointsEvolution] = await Promise.all([
          getCategoryStats().catch(() => null),
          getEnrolledModules().then((d) => (Array.isArray(d) ? d : [])),
          getAvailableModules().then((d) => (Array.isArray(d) ? d : [])),
          getPointsEvolution().catch(() => null),
        ]);
        commit("SET_DASHBOARD_DATA", {
          stats: stats && typeof stats === "object" ? stats : null,
          enrolledModules,
          availableModules,
          pointsEvolution: pointsEvolution && typeof pointsEvolution === "object" ? pointsEvolution : null,
        });

        getEnrolledModulesProgress().then((progress) => {
          if (Array.isArray(progress) && progress.length > 0) {
            commit("SET_ENROLLED_MODULES_PROGRESS", progress);
          }
        });
        
        return {
          stats: state.stats,
          enrolledModules: state.enrolledModules,
          availableModules: state.availableModules,
          pointsEvolution: state.pointsEvolution,
        };
      } finally {
        commit("CLEAR_FETCH_PROMISE");
      }
    })();
    commit("SET_FETCH_PROMISE", promise);
    return promise;
  },
  clearCache({ commit }) {
    commit("CLEAR_CACHE");
  },
  incrementExercisesCompleted({ commit }) {
    commit("INCREMENT_EXERCISES_COMPLETED");
  },
};
